/**
 * DataSourceDetailPage Component
 *
 * Page for viewing a single data source with its details and indexed documents.
 */
import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Globe,
  FileSpreadsheet,
  BookOpen,
  Calendar,
  Hash,
  AlertCircle,
  ExternalLink,
  Loader2,
  Tag,
  FileText,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { dataSourcesApi } from '@/services/api/dataSources';
import { FeedSyncCard } from '@/components/data-sources/FeedSyncCard';
import { isFeedSource } from '@/hooks/useFeedSync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { DataSourceStatus, DataSourceType } from '@/types/dataSource';

/**
 * Get badge variant for data source status
 */
function getStatusVariant(status: DataSourceStatus): 'pending' | 'processing' | 'completed' | 'failed' {
  switch (status) {
    case 'PENDING':
      return 'pending';
    case 'PROCESSING':
      return 'processing';
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    default:
      return 'pending';
  }
}

/**
 * Get human-readable status text
 */
function getStatusText(status: DataSourceStatus): string {
  switch (status) {
    case 'PENDING':
      return 'Ожидание';
    case 'PROCESSING':
      return 'Обработка';
    case 'COMPLETED':
      return 'Завершён';
    case 'FAILED':
      return 'Ошибка';
    default:
      return status;
  }
}

/**
 * Get icon for data source type
 */
function getTypeIcon(type: DataSourceType) {
  switch (type) {
    case 'EXCEL':
      return FileSpreadsheet;
    case 'WEB':
      return Globe;
    case 'WIKI':
      return BookOpen;
    default:
      return FileText;
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Не проиндексирован';
  return new Date(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Extract filename from file path or URL
 */
function extractFileName(filePath: string): string {
  const parts = filePath.split('/');
  return parts[parts.length - 1] || filePath;
}

/** Polling cadence for the indexing-run while the source is still processing. */
const INDEXING_POLL_INTERVAL_MS = 4000;

/**
 * DataSourceDetailPage is the page for viewing a single data source.
 */
export function DataSourceDetailPage() {
  const { id } = useParams<{ id: string }>();

  // Fetch data source details
  const {
    data: dataSource,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['dataSource', id],
    queryFn: () => dataSourcesApi.getById(id!),
    enabled: !!id,
    // While the source is being (re)indexed, keep refreshing so the
    // "Индексация" tab flips from "идёт" to the finished schema.
    refetchInterval: (query) =>
      query.state.data?.status === 'PROCESSING' ? INDEXING_POLL_INTERVAL_MS : false,
  });

  // Set document title
  useEffect(() => {
    if (dataSource) {
      document.title = `${dataSource.name} - Источники данных - SMETA AI`;
    } else {
      document.title = 'Источник данных - SMETA AI';
    }
  }, [dataSource]);

  const TypeIcon = dataSource ? getTypeIcon(dataSource.type) : null;

  return (
    <PageLayout
      data-testid="data-source-detail-page"
      titleIcon={
        TypeIcon ? (
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <TypeIcon className="h-5 w-5 text-white" />
          </div>
        ) : undefined
      }
      title={dataSource?.name || 'Источник данных'}
      subtitle={dataSource?.description}
      breadcrumbs={
        dataSource
          ? [
              { label: 'Главная', href: '/' },
              { label: 'Источники данных', href: '/data-sources' },
              { label: dataSource.name },
            ]
          : undefined
      }
      backButton={{ href: '/data-sources' }}
      titleAddon={
        dataSource ? (
          <Badge variant={getStatusVariant(dataSource.status)}>{getStatusText(dataSource.status)}</Badge>
        ) : undefined
      }
      stickyHeader={false}>
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Загрузка...</span>
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <p className="text-gray-600 mb-2">Ошибка загрузки источника данных</p>
          <p className="text-sm text-gray-500 mb-4">
            {error instanceof Error ? error.message : 'Неизвестная ошибка'}
          </p>
          <Link to="/data-sources">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />К списку источников
            </Button>
          </Link>
        </div>
      ) : !dataSource ? (
        <div className="flex flex-col items-center justify-center h-64">
          <FileText className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">Источник данных не найден</p>
          <Link to="/data-sources">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />К списку источников
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Info Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Type */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 mb-1">Тип</p>
                <div className="flex items-center gap-2">
                  {TypeIcon && <TypeIcon className="h-4 w-4 text-gray-600" />}
                  <Badge variant="secondary">{dataSource.type_display}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Status */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 mb-1">Статус</p>
                <Badge variant={getStatusVariant(dataSource.status)}>
                  {getStatusText(dataSource.status)}
                </Badge>
              </CardContent>
            </Card>

            {/* Document Count */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 mb-1">Документов</p>
                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-gray-600" />
                  <p className="text-sm font-medium text-gray-900">{dataSource.document_count}</p>
                </div>
              </CardContent>
            </Card>

            {/* Indexed At */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 mb-1">Проиндексирован</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <p className="text-sm font-medium text-gray-900">
                    {dataSource.indexed_at
                      ? new Date(dataSource.indexed_at).toLocaleDateString('ru-RU')
                      : 'Не проиндексирован'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Created At */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-gray-500 mb-1">Создан</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(dataSource.created_at).toLocaleDateString('ru-RU')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* KAN-738: ночное и полное обновление каталога (только FEED) */}
          {isFeedSource(dataSource) && <FeedSyncCard dataSource={dataSource} />}

          {/* Source Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Детали источника</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* WEB: Show URL */}
              {dataSource.type === 'WEB' && dataSource.url && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">URL источника</p>
                  <a
                    href={dataSource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 text-sm">
                    {dataSource.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* EXCEL: Show file name */}
              {dataSource.type === 'EXCEL' && dataSource.file && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Файл</p>
                  <div className="flex items-center gap-2 text-sm text-gray-900">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    {extractFileName(dataSource.file)}
                  </div>
                </div>
              )}

              {/* WIKI: Show content preview */}
              {dataSource.type === 'WIKI' && dataSource.content && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Содержимое</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {dataSource.content}
                  </div>
                </div>
              )}

              {/* Description */}
              {dataSource.description && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Описание</p>
                  <p className="text-sm text-gray-900">{dataSource.description}</p>
                </div>
              )}

              {/* Error message for FAILED status */}
              {dataSource.status === 'FAILED' && dataSource.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <p className="text-sm font-medium text-red-800">Ошибка индексации</p>
                  </div>
                  <p className="text-sm text-red-700">{dataSource.error_message}</p>
                </div>
              )}

              {/* Tags */}
              {dataSource.tags && dataSource.tags.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Теги</p>
                  <div className="flex flex-wrap gap-2">
                    {dataSource.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        <Tag className="h-3 w-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Created by */}
              {dataSource.created_by_name && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Создал</p>
                  <p className="text-sm text-gray-900">{dataSource.created_by_name}</p>
                </div>
              )}

              {/* Full timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                <div>
                  <p className="text-xs text-gray-500">Создан</p>
                  <p className="text-sm text-gray-700">{formatDate(dataSource.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Обновлён</p>
                  <p className="text-sm text-gray-700">{formatDate(dataSource.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageLayout>
  );
}

export default DataSourceDetailPage;
